import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import uuid from "react-uuid";

import { CreateFeedContainer } from "../../../component/styled/feedWrite";

import { useModal } from "../../../../../hooks/useModal";
import { hideLoading, showLoading } from "../../../../../store/loading";

import Info from "../../../component/feedWrite/info";
import Form from "../../../component/feedWrite/form";
import Actions from "../../../component/feedWrite/actions";
import useUploadFiles from "../../../../../hooks/useUploadFiles";

const Write = ({ group, member, openModal, closeModal, active, setActive }) => {
  const user = useSelector((state) => state?.user?.data[0]);

  const { uploadFiles, dataURItoBlob, readFileAsDataURL } = useUploadFiles();

  const { id } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const origin = location?.state?.feed;

  const [my, setMy] = useState([]);
  const [feed, setFeed] = useState({
    nickNm: "",
    gbn: group.groupNo,
    groupName: group.groupName,
    imgUrl: [],
    content: "",
  });
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!user) {
      toast.error("로그인 후 이용 가능합니다.", { toastId: "no-access" });
      navigate("/group");
    } else {
      getMyGroup();
    }

    if (origin) {
      const updated =
        (origin.imgUrl &&
          JSON.parse(origin.imgUrl).map((item) => ({
            path: item,
            id: uuid(),
          }))) ||
        [];

      setFeed({
        ...origin,
        imgUrl: updated,
        content: origin.realContent ? origin.realContent : origin.content || "",
      });
      // setFiles(Array.from({ length: updated.length }).fill(""));
    }
  }, []);

  const getMyGroup = async () => {
    const url = "/api/groupCondList";
    const req = getDataCond();
    const res = await axios.post(url, req);
    if (res.status === 200) {
      setMy(res.data.content);
    }
  };

  const getDataCond = () => {
    return {
      mberNo: user.mberNo,
      delYn: "N",
      offset: 0,
      pageNumber: 1,
      pageSize: 1,
      paged: false,
    };
  };

  const checkValidation = async () => {
    if (feed.feedNo) {
      const delCheck = await feedDelYnCheck(feed.feedNo);
      if (delCheck === "Y") {
        toast.error("게시물이 존재하지 않습니다.", { toastId: "no-access" });
        navigate(`/group/${id}`);
        return false;
      }
    }

    if (feed.gbn === "0" || !feed.gbn) {
      toast.error("모임을 선택해주세요.");
      return false;
    }

    if (!feed.content || !feed.content.trim() || !feed.content.replaceAll("\n", "")) {
      toast.error("내용이 존재하지 않습니다.");
      return false;
    }
    return true;
  };

  const replaceImages = (localData, serverData) => {
    const updatedData = localData.map((local) => {
      const matched = serverData.find((server) => local.fileNm && server.origin === local.fileNm);

      if (matched) {
        return {
          path: matched.fileName,
        };
      }

      return local;
    });

    return updatedData.map((item) => item.path);
  };

  const handleSubmit = async () => {
    if (!(await checkValidation())) {
      closeModal();
      return;
    }

    try {
      let uploadedImages;
      if (feed.imgUrl.length > 0) {
        const serverData = (await uploadImages()) || [];
        uploadedImages = replaceImages(feed.imgUrl, serverData);
      }

      dispatch(showLoading());

      const url = "/api/feedList";
      const body = {
        content: feed.content.trim(),
        imgUrl: uploadedImages ? JSON.stringify(uploadedImages) : JSON.stringify([]),
        mberNo: user.mberNo,
        mberNm: user.name,
        delYn: "N",
        gbn: feed.gbn,
        createMberId: user.userName,
        updateMberId: user.userName,
      };

      if (feed.feedNo) {
        body.feedNo = feed.feedNo;
      }

      const res = await axios.put(url, body);

      if (res.status === 200) {
        toast.success(`게시글이 ${feed.createDt ? "수정" : "작성"}되었습니다.`);
        setActive(0);
        closeModal();
      }
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(hideLoading());
    }
  };

  const changeImg = async (e) => {
    const files = e.target.files;

    if (files) {
      const form = {
        images: [],
        files: [],
      };

      for (const item of files) {
        const id = uuid();
        const path = await readFileAsDataURL(item);

        form.images.push({ fileNm: item.name, type: item.type, path, id });
        form.files.push({ id, file: item });
      }

      setFeed((data) => ({ ...data, imgUrl: [...data.imgUrl, ...form.images] }));
      setFiles((data) => [...data, ...form.files]);
    }
  };

  // 이미지 업로드
  const uploadImages = async () => {
    try {
      const blobs = files.map((f) => {
        if (f.file instanceof File) {
          return f.file;
        }

        const blob = dataURItoBlob(f.path);
        return new File([blob], `${uuid()}.png`, { type: blob.type });
      });

      const response = await uploadFiles(blobs);
      if (!response || response.length === 0) return [];

      const originalFileNames = blobs.map((b) => b.name.split(".").slice(0, -1).join("."));

      return response.map((item, idx) => {
        const uploadedFileName = item.fileName.split("/").pop().split(".").slice(0, -1).join(".");
        const isSameName = originalFileNames.includes(uploadedFileName);
        return {
          ...item,
          origin: isSameName ? blobs[idx].name : undefined,
        };
      });
    } catch (error) {
      console.error("Error uploading images:", error);
      return [];
    }
  };

  // 수정 완료 시 삭제여부
  const feedDelYnCheck = async (feedNo) => {
    const url = "/api/feedList";
    const request = {
      feedNo,
      offset: 0,
      pageNumber: 0,
      pageSize: 10,
      paged: true,
    };

    const res = await axios.post(url, request);
    return res?.data?.content[0].delYn;
  };

  // 클립보드
  const handlePaste = async (e) => {
    const items = e.clipboardData.items;
    const imageFilePromises = [];

    for (let item of items) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        const file = item.getAsFile();
        const mockEvent = { target: { files: [file] } };
        imageFilePromises.push(changeImg(mockEvent));
      }
    }
  };

  const deleteImages = (item) => {
    openModal({
      isOpen: true,
      type: "confirmation",
      title: "이미지 삭제",
      message: [`이미지를 삭제하시겠습니까?`],
      handleConfirm: () => {
        setFeed((data) => ({
          ...data,
          imgUrl: [...data.imgUrl].filter((ele) => ele.id !== item.id),
        }));
        setFiles(files.filter((img) => img.id !== item.id));
        closeModal();
      },
      handleCancel: () => closeModal(),
    });
  };

  return (
    <>
      <CreateFeedContainer>
        <Info feed={feed} setFeed={setFeed} location={location} myGrp={my} changeImg={changeImg} />
        <Form
          member={member}
          feed={feed}
          setFeed={setFeed}
          handleModal={deleteImages}
          handlePaste={handlePaste}
        />
        <Actions
          feed={feed}
          openModal={openModal}
          closeModal={closeModal}
          handleSubmit={handleSubmit}
          setActive={setActive}
        />
      </CreateFeedContainer>
    </>
  );
};

export default Write;
